"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useState } from "react";
import { RavioliMedia } from "../RavioliMedia/RavioliMedia";
import type { IVideoPlayerProps } from "./VideoPlayer.props";

export const VideoPlayer = ({
  banner,
  directVideo,
  source,
  vimeoVideo,
  youtubeVideo,
}: IVideoPlayerProps) => {
  const video =
    source === "direct" ? directVideo : source === "youtube" ? youtubeVideo : vimeoVideo;
  const [playing, setPlaying] = useState(() => !banner);
  if (!video) return null;

  const shouldAutoplay = Boolean(banner && playing);
  const playerSource = shouldAutoplay
    ? `${video}${video.includes("?") ? "&" : "?"}autoplay=1`
    : video;
  const player =
    source === "direct" ? (
      <video
        autoPlay={shouldAutoplay}
        className="absolute inset-0 h-full w-full bg-ink object-cover"
        controls
        playsInline
        preload="metadata"
        src={video}
      >
        Your browser does not support embedded video.
      </video>
    ) : (
      <iframe
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        src={playerSource}
        title={`${source === "youtube" ? "YouTube" : "Vimeo"} video`}
      />
    );

  return (
    <div className="relative aspect-video overflow-hidden border border-primary bg-ink shadow-ravioli-action-lg">
      {!playing ? (
        <button
          aria-label="Play video"
          className="ravioli-motion-travel group absolute inset-0 block h-full w-full cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          onClick={() => setPlaying(true)}
          type="button"
        >
          {banner ? (
            <>
              <RavioliMedia
                aspectRatio="16:9"
                className="pointer-events-none h-full"
                imageClassName="object-cover transition-transform duration-200 ease-out group-hover:scale-[1.015]"
                media={banner.image}
                sizes="(min-width: 1280px) 1184px, 100vw"
              />
              <span aria-hidden="true" className="absolute inset-0 bg-ink/25" />
            </>
          ) : (
            <span className="absolute inset-0 flex flex-col justify-between bg-collection p-6 text-ink md:p-8">
              <span className="inline-flex w-fit items-center gap-2 border border-primary bg-surface px-3 py-1.5 font-ravioli-body text-sm font-semibold text-primary">
                <span aria-hidden="true" className="size-1.5 bg-primary" />
                Video
              </span>
              <span>
                <span className="block max-w-[14ch] font-ravioli-display text-3xl font-semibold leading-[1.02] md:text-5xl">
                  Play the full story.
                </span>
                <span className="mt-2 block font-ravioli-body text-sm text-ink-muted md:text-base">
                  Open the player when you are ready.
                </span>
              </span>
            </span>
          )}
          <span
            className={cn(
              "absolute inline-flex size-16 items-center justify-center border border-primary bg-surface text-primary shadow-ravioli-action-sm transition-transform duration-200 group-hover:scale-105",
              banner ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "right-6 top-6",
            )}
          >
            <Icon className="ml-1 size-6" name="play" />
          </span>
        </button>
      ) : (
        player
      )}
    </div>
  );
};

VideoPlayer.displayName = "Content::VideoPlayer";
